import { memo } from 'react';
import { cn } from '@/lib/utils';
import { Paper } from '@/types/paper';
import PaperCardHeader from './card/PaperCardHeader';
import PaperCardTitle from './card/PaperCardTitle';
import PaperCardAbstract from './card/PaperCardAbstract';
import PaperCardCategories from './card/PaperCardCategories';
import PaperCardFooter from './card/PaperCardFooter';
import { useCardHoverEffect } from './card/useCardHoverEffect';
import { usePaperPrefetch } from '@/hooks/papers/usePaperPrefetch';

interface PaperCardProps {
  paper: Paper;
  featured?: boolean;
  variant?: 'grid' | 'list';
  compactFeatured?: boolean;
}

const PaperCard = memo(({ paper, featured = false, variant = 'grid', compactFeatured = false }: PaperCardProps) => {
  const { cardRef, isHovered, setIsHovered } = useCardHoverEffect(featured);
  const { prefetchPaper, cancelPrefetch } = usePaperPrefetch();

  // Check if we have categories/keywords to display
  const hasCategories = paper.category || (paper.keywords && paper.keywords.length > 0);

  const handleMouseEnter = () => {
    setIsHovered(true);
    // Prefetch paper data when hovering
    prefetchPaper(paper.id);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    cancelPrefetch();
  };

  return (
    <div
      ref={cardRef}
      role="article"
      className={cn(
        "paper-card group relative cursor-pointer flex flex-col h-full",
        "bg-white rounded-xl border border-border/50 shadow-sm",
        "hover:shadow-lg hover:border-primary/20 hover:-translate-y-1",
        "transition-all duration-300 ease-out",
        featured 
          ? "p-4 md:p-6 lg:p-8 shadow-md" 
          : "p-4 md:p-5"
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="paper-card"
    >
      {/* Card Header with metadata */}
      <PaperCardHeader paper={paper} />
      
      {/* Card Title - Use HoverCard for abstract preview */}
      <PaperCardTitle paper={paper} featured={featured} />
      
      {/* Static Abstract Preview - Only for featured cards */}
      <PaperCardAbstract paper={paper} featured={featured} compact={compactFeatured} />
      
      {/* Categories and Keywords - Only show if content exists */}
      {hasCategories && (
        <PaperCardCategories paper={paper} />
      )}
      
      {/* Card Footer - Use mt-auto to push to bottom */}
      <div className="mt-auto">
        <PaperCardFooter paper={paper} featured={featured} />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  // Check if paper data actually changed
  if (prevProps.paper.id !== nextProps.paper.id) return false;
  if (prevProps.featured !== nextProps.featured) return false;
  if (prevProps.variant !== nextProps.variant) return false;
  if (prevProps.compactFeatured !== nextProps.compactFeatured) return false;
  
  // Check if dynamic paper data changed
  if (prevProps.paper.updatedAt !== nextProps.paper.updatedAt) return false;
  if (prevProps.paper.views !== nextProps.paper.views) return false;
  if (prevProps.paper.reviewCount !== nextProps.paper.reviewCount) return false;
  if (prevProps.paper.commentCount !== nextProps.paper.commentCount) return false;
  if (prevProps.paper.averageRating !== nextProps.paper.averageRating) return false;
  
  return true; // Don't re-render
});

PaperCard.displayName = 'PaperCard';

export default PaperCard;
